import { z } from 'zod';
import superjson from 'superjson';

export const schema = z.object({
  sessionId: z.string().min(1),
  goal: z.string().min(1), // User's high-level goal
  maxIterations: z.number().optional().default(5), // Max planner→builder→reviewer→qa loops
  autoExecute: z.boolean().optional().default(false), // Auto-apply code changes
});

export type InputType = z.infer<typeof schema>;

export type AgentMessage = {
  agent: 'conductor' | 'planner' | 'builder' | 'reviewer' | 'qa';
  content: string;
  timestamp: string;
};

export type TaskItem = {
  id: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'blocked';
  files: string[];
  complexity: 'simple' | 'medium' | 'complex';
};

export type OutputType = {
  success: boolean;
  sessionId: string;
  goal: string;
  iterations: number;
  tasks: TaskItem[];
  messages: AgentMessage[];
  summary: string;
  error?: string;
};

export const runConductor = async (body: InputType, init?: RequestInit): Promise<OutputType> => {
  const validatedInput = schema.parse(body);
  const result = await fetch(`/_api/agents/conductor`, {
    method: 'POST',
    credentials: 'include',
    body: superjson.stringify(validatedInput),
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  const responseText = await result.text();

  if (!result.ok) {
    // Try to parse error response
    if (!responseText || responseText.trim() === '') {
      throw new Error(`Agent team failed: Server returned empty response (${result.status})`);
    }
    try {
      const errorObject = superjson.parse<{ error: string }>(responseText);
      throw new Error(errorObject.error || 'Unknown error');
    } catch (parseError) {
      // If parsing fails, use the raw text
      throw new Error(`Agent team failed: ${responseText.substring(0, 200)}`);
    }
  }

  // Parse successful response
  if (!responseText || responseText.trim() === '') {
    throw new Error('Agent team failed: Empty response from server');
  }

  try {
    return superjson.parse<OutputType>(responseText);
  } catch (parseError) {
    console.error('[Conductor] Failed to parse response:', responseText.substring(0, 500));
    throw new Error(
      `Agent team failed: Invalid response format - ${(parseError as Error).message}`,
    );
  }
};
